import React, { useEffect, useState } from "react";

import { Calendar, ChevronRight, Users } from "lucide-react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import { Card } from "../../../../shared/card/card.tsx";
import { LinearProgress } from "../../../../ui/linear-progress/linear-progress.tsx";

import { useMyProjects } from "../../../../../hooks/use-my-projects.ts";
import type { MyProjectItem, ProjectStatus } from "./types.ts";
import styles from "./my-projects.module.css";

const ANIM_MS = 700;

function easeOutQuad(t: number): number {
  return 1 - (1 - t) * (1 - t);
}

function useAnimatedPercent(target: number, durationMs: number): number {
  const [value, setValue] = useState(0);

  useEffect(() => {
    const clamped = Math.min(100, Math.max(0, Math.round(target)));
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches || durationMs <= 0) {
      setValue(clamped);
      return;
    }

    let raf = 0;
    const start = performance.now();

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      setValue(Math.round(easeOutQuad(t) * clamped));
      if (t < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);

  return value;
}
function ProjectProgress({
  progressPercent,
  isOngoing,
  status,
}: {
  progressPercent: number;
  isOngoing: boolean;
  status: ProjectStatus;
}) {
  const animated = useAnimatedPercent(
    isOngoing ? 50 : progressPercent,
    ANIM_MS,
  );
  if (isOngoing) {
    return (
      <span
        className={clsx(
          "heading-xs",
          styles.percentAccent,
          styles.ongoingLabel,
        )}
      >
        Ongoing
      </span>
    );
  }

  return (
    <span
      className={clsx(
        "heading-xs",
        status === "completed" ? styles.percentSuccess : styles.percentAccent,
      )}
    >
      {animated}%
    </span>
  );
}

function statusLabel(status: ProjectStatus): string {
  if (status === "completed") return "Completed";
  if (status === "onhold") return "On Hold";
  return "Active";
}
function badgeClass(status: ProjectStatus): string {
  if (status === "completed") return styles.badgeCompleted;
  if (status === "onhold") return styles.badgeOnHold;
  return styles.badgeActive;
}

function ProjectRow({ item, index }: { item: MyProjectItem; index: number }) {
  return (
    <article
      className={clsx(styles.row, index === 0 && styles.firstRow)}
      aria-label={item.name}
    >
      {/* Name + badge */}
      <div className={styles.rowTop}>
        <p className={clsx("heading-xs", "mb-0", styles.truncate)}>
          {item.name}
        </p>
        <span className={clsx("sub-heading-xs", badgeClass(item.status))}>
          {statusLabel(item.status)}
        </span>
      </div>

      {/* Progress bar */}
      <div className={styles.progressWrap}>
        <LinearProgress
          value={item.isOngoing ? 50 : item.progressPercent}
          variant={item.status === "completed" ? "success" : "accent"}
          showValue={false}
          durationMs={ANIM_MS}
          aria-label={`${item.name} progress`}
        />
      </div>

      {/* Meta row */}
      <div className={styles.rowBottom}>
        <div className={styles.metaLeft}>
          <span className={styles.metaItem}>
            <Users size={14} aria-hidden />
            <span className="sub-heading-sm">{item.memberCount} members</span>
          </span>

          {item.dueDateLabel && (
            <span className={styles.metaItem}>
              <Calendar size={14} aria-hidden />
              <span className="sub-heading-sm">{item.dueDateLabel}</span>
            </span>
          )}
        </div>

        <ProjectProgress
          progressPercent={item.progressPercent}
          isOngoing={item.isOngoing}
          status={item.status}
        />
      </div>
    </article>
  );
}

export function MyProjects() {
  const { projects, loading, error } = useMyProjects();

  return (
    <Card variant="default" padding="5xl" className={styles.myProjectsCard}>
      {/* Header */}
      <div className={styles.header}>
        <h6 className="heading-sm mb-0">My Projects</h6>
        <Link
          to="/dashboard/myProject"
          className={clsx(styles.viewAllButton, "sub-heading-sm")}
          aria-label="View all projects"
        >
          View all
          <ChevronRight size={14} aria-hidden />
        </Link>
      </div>
 
      {/* States */}
      {loading && (
        <p className={clsx("sub-heading-sm", styles.stateText)}>
          Loading projects…
        </p>
      )}

      {!loading && error && (
        <p
          className={clsx("sub-heading-sm", styles.stateText, styles.errorText)}
        >
          {error}
        </p>
      )}

      {!loading && !error && projects.length === 0 && (
        <p className={clsx("sub-heading-sm", styles.stateText)}>
          No Projects Assigned.
        </p>
      )}

      {/* Project rows — scrollable after max-height */}
      {!loading && !error && projects.length > 0 && (
        <div className={styles.projectList}>
          {projects.map((item, index) => (
            <ProjectRow key={item.id} item={item} index={index} />
          ))}
        </div>
      )}
    </Card>
  );
}
